//Screen Functions ###################################

import type {
  CustomValidatorsType,
  DirectiveResult,
  SYSDialog,
  SYSFeature,
  SYSForm,
  SYSFormValidationValidators,
  SYSScreen,
  SYSTableDefIds,
  TemplateResult,
} from "../"

import type {
  SYSField,
  SYSFormState,
  SummaryErrorList,
} from "../library/forms/actions"
import type { LookupListPostData } from "../library/forms/controls/helpers"

import { DialogsState } from "../library/dialog/sysdialogbase"
import type { SYSFormField, Schema } from "../library/forms/sysformelement"
import type { FileUploadContainer } from "../library/table/actions"
import type { CellClass } from "../library/table/row"
import type { GroupHeaderFooterBreaksValueType } from "../library/table/rowwrapper"
import type {
  ColumnDisplayDef,
  DisplayMode,
  DragDropEventType,
  EditDisplayMode,
  RenderingGroups,
  EditMode,
  GroupBreakType,
  ListDefinition,
  RowFunctionResultToSet,
  SYSTableData,
  SYSTableDef,
  SaveMode,
  SelectRowDefMode,
  TableClickModifiersType,
  TableDataAndDef,
  ViewRowClassType,
  _ColumnVisibility,
} from "../library/table/table"
import { _TableGetReturnType } from "../library/table/tableapi"

import {
  OptionsValidate,
  ValidateResultError,
  ValidationsAddOptions,
  ValidatorOptions,
  ValidatorUseOptions,
} from "../library/forms/validators"

export type HookFormSummaryValidationTextDisplay_Type = {
  formState: SYSFormState
  relevantErrors: SummaryErrorList[]
}

export type HookFormSummaryValidationTextDisplay_ReturnType = {
  // default and implmeneted: All the Errors
  res?: TemplateResult | TemplateResult[]
}

// subFeature X
export const HookFormSummaryValidationTextDisplay =
  "HookForm{subFeature}SummaryValidationTextDisplay"

export type HookFormValidatorsCustom_Type = {
  field: SYSField
  options: ValidatorOptions
  validator: CustomValidatorsType
}

export type HookFormValidatorsCustom_ReturnType = boolean
// subFeature unclear
export const HookFormValidatorsCustom = "HookFormValidatorsCustom"

export type HookFormValidatorsDefaults_Type = {
  defaultsToSet: OptionsValidate
  formState: SYSFormState
}
// subFeature unclear
export const HookFormValidatorsDefaults = "HookFormValidatorsDefaults"

export type HookFieldValidationTextDisplay_Type = {
  formState: SYSFormState
  field: SYSField
  visibleErrors: ValidateResultError[]
}
export type HookFieldValidationTextDisplay_ReturnType = {
  res?: TemplateResult | TemplateResult[]
}

//subFeature X
export const HookFieldValidationTextDisplay =
  "HookField{subFeature}ValidationTextDisplay"

export type HookAppUrlResolver_Type = {
  apiUrlToSet: string
  wsUrlToSet: string
  isDemoToSet: boolean
  isTestingToSet: boolean
  cleanUrlToSet: boolean
}

export const HookAppUrlResolver = "HookAppUrlResolver"

export type HookAppFileOpen_Type = {
  blob: any
  mimeType: string
  feature: SYSFeature
  fileName: string
  responseHeaders: any
}

export const HookAppFileOpen = "HookAppFileOpen"

export const HookAppReady = "HookAppReady"
export const HookAppInit = "HookAppInit"
export const HookAppStateReady = "HookAppStateReady"
export const HookAppRouting = "HookAppRouting"
export const HookAppAfterRehydrate = "HookAppAfterRehydrate"
export const HookAppSaveState = "HookAppSaveState"

export type _HookTableInitToSet = {
  ForceInitColumns?: boolean
  ForceInitDef?: boolean
}

export type HookTableInit_Type = {
  context: "before" | "after"
  tableDef: SYSTableDef
  isInitialised?: boolean
  toSet: _HookTableInitToSet
}

export const HookTableInit = "HookTableInit"

export type HookTableSchemaAssign_Type<RowType = any> = {
  tableDefId: SYSTableDefIds
  schemaToSet: { [key in keyof RowType]: Schema }
}
export const HookTableSchemaAssign = "HookTableSchemaAssign"

export type HookTableDragDrop_Type<RowType = any> = {
  type: DragDropEventType
  e: DragEvent
  row: { [key in keyof RowType]: any }
  key: string
  el: HTMLElement
  formState: SYSFormState
  columnKey: keyof RowType
  tableDefId: SYSTableDefIds
}
export const HookTableDragDrop = "HookTableDragDrop"

// the eventhandler if the user selected/clicked the cell
export type HookDialogClose_Type = {
  dialog: SYSDialog
  dialogState: DialogsState
  dialogInstance: any
}
export type HookDialogClose_ReturnType = boolean
export const HookDialogClose = "HookDialogClose"

export type HookDialogActivate_Type = HookDialogClose_Type
export const HookDialogActivate = "HookDialogActivate"

type NavDirection = "Back" | "Forward"
// gets called when user navigates into a screen
export type HookScreenNavigateIn_ReturnType = boolean

export type HookScreenNavigateIn_Type = {
  navDirection: NavDirection
  screen: SYSScreen
  fromScreen: SYSScreen
  instance?: any
}
export type HookScreenNavigateOut_Type = {
  navDirection: NavDirection
  screen: SYSScreen
  toScreen: SYSScreen
  instance?: any
}
export const HookScreenNavigateIn = "HookScreenNavigateIn"

export type HookScreenNavigateOut_ReturnType = boolean
export const HookScreenNavigateOut = "HookScreenNavigateOut"

//Default interceptable Functions
export type HookTableFn_ReturnType = boolean | void
export type HookTableFnTypes =
  | "ColumnFilter"
  | "ColumnRangeFilter"
  | "CustomFilter"
  | "AttributeFilter"
  | "Filter"
  | "FilterSelected"
  | "ColumnSort"
  | "CustomSort"
  | "CustomColumnFunction"
  | "TableSettingsApplyFilterSort"
  | "SelectAll"

export type HookTableFn_Type = {
  TableDefId: SYSTableDefIds
  FnType: HookTableFnTypes
  // contains keys like column keys, filtered keys, ...
  id?: any
  val1?: any
  val2?: any
  // if started for a selection this is true
  selection?: any
  // in before context we can return cancel = true from our functions to not start the fn
  before: boolean
  // if called from ovl itself and is not triggered directly by user action
  innerEvent?: boolean
}
export const HookTableFn = "HookTableFn"

//Form Functions ####################################

export type HookFormInit_Type<RowType = any> = {
  formState: SYSFormState
  fields: { [key in keyof RowType]: SYSField }
}
export type HookFormInit_ReturnType =
  | undefined
  | {
      fieldsToRemove?: string[]
      groupsToRemove?: string[]
    }

// subFeature X
export const HookFormInit = "HookForm{subFeature}Init"

export type HookFormRemove_Type<RowType = any> = {
  formState: SYSFormState
  fields: { [key in keyof RowType]: SYSField }
}

// subFeature x
export const HookFormRemove = "HookForm{subFeature}Remove"

export type FieldValueChanged<RowType = any> = {
  isValid: boolean
  field: SYSField
  fieldKey: keyof RowType
  oldValue: any
  newValue: any
  oldValidValue: any
  validValueChanged: boolean
  fromInit: boolean
  tab: number
  group: number
}

export type ValidatorCheckOptions = {
  field: SYSField
  val: any
}

export type HookFormChanged_Type<RowType = any> = {
  formState: SYSFormState
  fields: { [key in keyof RowType]: SYSField }
  row: RowType
  validatorUse: (
    validator: SYSFormValidationValidators,
    options?: ValidatorUseOptions
  ) => boolean
  validatorCheck: (
    validator: SYSFormValidationValidators,
    options?: ValidatorCheckOptions
  ) => boolean
  validationsAdd: (
    translationKey: string,
    options?: ValidationsAddOptions
  ) => void
  validationsRemove: (
    fields: SYSField | SYSField[],
    valisationKey: string
  ) => boolean

  setCorrectedValue: (
    val: any,
    ignoreCustomValidation?: boolean,
    select?: boolean
  ) => Promise<void>

  initiatedByFormInit?: boolean
  changedField?: FieldValueChanged<RowType>
}
//subFeature x
export const HookFormChanged = "HookForm{subFeature}Changed"

// // gets called whenever detailview rendered
// export type HookViewAfterRender_Type = { view: ViewRowDef }
// export const HookViewAfterRender = "ViewAfterRender"

export type HookFormRenderingAfterRender_Type = {
  formState: SYSFormState
  comp: HTMLElement
}
// subFeature x
export const HookFormRenderingAfterRender =
  "HookForm{subFeature}RenderingAfterRender"

export type HookFormRenderingFirstUpdated_Type = {
  formState: SYSFormState
  comp: HTMLElement
}

// subFeature x
export const HookFormRenderingFirstUpdated =
  "HookForm{subFeature}RenderingFirstUpdated"

export type HookFormRenderingUpdated_Type = {
  formState: SYSFormState
  comp: HTMLElement
}

// subFeature x
export const HookFormRenderingUpdated = "HookForm{subFeature}RenderingUpdated"

// same for table

export type HookTableRenderingAfterRender_Type = {
  defId: SYSTableDefIds
  comp: HTMLElement
}
export const HookTableRenderingAfterRender = "HookTableRenderingAfterRender"

export type HookTableRenderingFirstUpdated_Type = {
  defId: SYSTableDefIds
  comp: HTMLElement
}
export const HookTableRenderingFirstUpdated = "HookTableRenderingFirstUpdated"

export type HookTableRenderingUpdated_Type = {
  defId: SYSTableDefIds
  comp: HTMLElement
}
export const HookTableRenderingUpdated = "HookTableRenderingUpdated"

export type HookFormDirtyChanged_Type = {
  formState: SYSFormState
  newDirty: boolean
}

// // subFeature x
// export const HookFormDirtyChanged = "HookForm{subFeature}DirtyChanged"

export type HookFormIsDirty_Type = {
  formState: SYSFormState
  dirtyFromFields: boolean
}
export type HookFormIsDirty_ReturnType = {
  forceIsDirtyTrue?: boolean
}

// subFeature x
export const HookFormIsDirty = "HookForm{subFeature}IsDirty"

// subFeature x
export const HookFormIsValid = "HookFormIsValid"
export type HookFormIsValid_Type = {
  formState: SYSFormState
  validFromFields: boolean
}
export type HookFormIsValid_ReturnType = {
  forceIsInvalidTrue?: boolean
}

// gets called when a form fields current value is changed by user input
export type HookFormValidate_Type<RowType = any> = {
  formState: SYSFormState

  field: SYSField
  fieldKey: keyof RowType
  fields: { [key in keyof RowType]: SYSField }
  oldValue: any
  newValue: any
  validSoFar: boolean
  row: RowType
  setCorrectedValue: (
    val: any,
    ignoreCustomValidation?: boolean,
    select?: boolean
  ) => Promise<void>
  validatorUse: (
    validator: SYSFormValidationValidators,
    options?: ValidatorUseOptions
  ) => boolean
  validatorCheck: (
    validator: SYSFormValidationValidators,
    options?: ValidatorCheckOptions
  ) => boolean
  validationsAdd: (
    translationKey: string,
    options?: ValidationsAddOptions
  ) => void
  group: number
  tab: number
}

// subFeature x
export const HookFormValidate = "HookFormValidate"
// gets called when formdata is added
// eg. useful if user defined some values in the FormDataAdd Hook which are not columns
// those values gets saved as well (think: nonvisible in form fields but client dictated defaults for add)
export type HookFormAdd_Type<RowType = any> = {
  newRow: RowType
  tableDef: SYSTableDef
  tableData: SYSTableData
  copyFromKey: string
}
export type HookFormAdd_ReturnType<RowType = any> = {
  fieldKeyToFocus?: keyof RowType
  cancelAction?: boolean
}
export const HookFormAdd = "HookFormAdd"
export type HookTableRowFnResultExecution_Type = {
  def: SYSTableDef
  data: SYSTableData
  fnKey: string
  fnName: string
  mode: "start" | "beforeResultHandler" | "afterResultHandler" | "end"
  currentResult: TableRowFnProcessFeedbackFull_Type
  executionContextToSet: {
    doNotExecuteSystemAfterResultHandler?: boolean
    doNotSetFeedbackOnProcess?: boolean
    custom?: any
  }
  currentFeedback: { [key: string]: TableRowFnProcessRawFeedback_Type }
}

export const HookTableRowFnResultExecution = "HookTableRowFnResultExecution"

export type TableRowFnProcessRawFeedbackSummaryCount_Type = "cis" | "ncis"
export type TableRowFnProcessRawFeedback_Type = {
  type: "error" | "warning" | "success" | "information" | "remove"
  text?: string | TemplateResult | DirectiveResult
  code?: string
  id?: string
  cnt?: TableRowFnProcessRawFeedbackSummaryCount_Type
  snacked?: boolean
  suppressFeedbackSnack?: boolean
}

export type TableRowFnProcessFeedback_Type =
  TableRowFnProcessRawFeedback_Type & {
    fieldsRes?: any
    // if any, put the known data entity directly here. It will be composed to an array and handed over to TableServerRefresh fn
    tableRefreshData?: any
  }

export type TableRowFnProcessFeedbackFull_Type =
  TableRowFnProcessFeedback_Type & {
    ts?: number
    fnId?: string //id of the function (needed when we need to create a process on the fly)
    defId?: SYSTableDefIds
    // if any, put specific fields into fieldsRes which needs to be processed separately by the hook
  }

// if it returns false no generic success snack will be displayed
export type HookTableRowFn_Type<RowType = any> = {
  isSingle: boolean
  fnKey: string
  rowKey: string
  def: SYSTableDef
  data: SYSTableData
  row: RowType
  rows: { [key: string]: RowType }
  selectedKeys: string[]
  resultToSet: RowFunctionResultToSet
  connectedConfirmFormState: SYSFormState
}
export const HookTableRowFn = "HookTableRowFn"

export type HookTableRowFnConfirmToSet_Type = {
  systemValidationContent: string | TemplateResult
  systemConfirmContent: string | TemplateResult
  customConfirmContent: string | TemplateResult
  // push invalidkeys to this array
  // if in confirm dialog and we need more control
  // usecase kaltag spcsymbol. should only be delete if not already used by customer
  invalidKeys: string[]
  cancelFn?: boolean
  noPrompt?: boolean
}

export type RowFnConfirmContentFieldsReset_Type =
  | "Never"
  | "ResultHasSuccessAndOrWarning"
  | "ResultHasOnlySuccess"
  | "Always"
// gets called for additional info or even a connected form when prompting rowfns (ok button disabled in OkCancel Dialog when form invalid)
export type HookTableRowFnConfirm_Type<RowType = any> = {
  connectedFormState?: SYSFormState
  defaultFormBodyRenderer: () => TemplateResult
  isMulti: boolean
  toSet: HookTableRowFnConfirmToSet_Type
  fnName: string
  fnKey: string
  tableDefId: SYSTableDefIds
  selectedKeys: string[]
  validKeys: string[]
  rows: { [key: string]: RowType }
}
export const HookTableRowFnConfirm = "HookTableRowFnConfirm"

import type { OvlSysColumn } from "../library/table/table"
import { GetRowValueOperation } from "../library/table/helpers"

// gets called when a form/row is selected and controls the row edit enabled
export type HookTableRowFnCan_Type<
  RowType = any,
  RowFnType extends
    | "SYSAdd"
    | "SYSEdit"
    | "SYSCopy"
    | "SYSDelete"
    | "SYSDetailView"
    | string
    | number
    | symbol = any,
> = {
  rowKey: string
  tableDef: SYSTableDef
  tableData: SYSTableData
  row: RowType & { OvlSysState?: OvlSysColumn }
  // set fun makes sure that only existing keys get set and that Transition from "NotVisible" to "Disabled" is not possible

  set: (
    fnKey:
      | (RowFnType | "SYSEdit" | "SYSCopy" | "SYSDelete" | "SYSDetailView")
      | Array<
          RowFnType | "SYSEdit" | "SYSCopy" | "SYSDelete" | "SYSDetailView"
        >,
    options: {
      visibility?: FormFnCan_Visibility
      tooltip?: string
      icon?: string
      customClass?: string
    }
  ) => void
  _functionsToSet: {}
}
export type FormFnCan_Visibility = "Visible" | "Disabled" | "NotVisible"

export const HookTableRowFnCan = "HookTableRowFnCan"

export type HookTablePrepareFn_Type = {
  type: "Filter" | "Sort" | "Column" | "Table"
  mode: "TableTitle" | "HeaderMenu"
  values: { [key: string]: { description: string } }
  def: SYSTableDef
  data: SYSTableData
  columnKey: string
}

export type HookTablePrepareFn_ReturnTypeEntry = {
  res?: TemplateResult | TemplateResult[] | DirectiveResult | string
  visibility?: FormFnCan_Visibility
  tooltip?: string
  icon?: string
}

export type HookTablePrepareFn_ReturnType = {
  SYSAdd?: HookTablePrepareFn_ReturnTypeEntry
  SYSDelete?: HookTablePrepareFn_ReturnTypeEntry
  SYSEdit?: HookTablePrepareFn_ReturnTypeEntry
  SYSCopy?: HookTablePrepareFn_ReturnTypeEntry
  SYSDetailView?: HookTablePrepareFn_ReturnTypeEntry
  [key: string]: HookTablePrepareFn_ReturnTypeEntry
}

export const HookTablePrepareFn = "HookTablePrepareFn"

// custom filter function
export type HookTableCustomFilter_Type<RowType = any> = {
  def: SYSTableDef
  //lang: string
  data: SYSTableData
  row: RowType
}
export type HookTableCustomFilter_ReturnType = boolean
export const HookTableCustomFilter = "HookTableCustom_{filterKey}_Filter"
// custom sort function
export type HookTableCustomSort_Type<RowType = any> = {
  def: SYSTableDef
  //lang: string
  a: string
  b: string
  data: { [key: string]: RowType }
}
export type HookTableCustomSort_ReturnType = number
export const HookTableCustomSort = "HookTableCustom_{sortKey}_Sort"

// defines custom column functions (eg. goto valid values)
export type HookTableColumnFn_Type = {
  columnKey: string
  fnKey: string
  def: SYSTableDef
  data: SYSTableData
  innerEvent?: boolean
}
export const HookTableColumnFn = "HookTableColumnFn"

//Form Field Functions ####################################
// defines the list of ListControl and Option so far

// sample for generic GetList approach to be done soon
// export type HookGetList_Type<RowType = any> = {
//   fieldKey: keyof RowType
//   formState: SYSFormState
//   needsFullList: boolean
// }
// export type HookGetList_ReturnType = ListDefinition
// export const HookGetList = "HookGetList"

export type HookFieldGetList_Type = {
  formState: SYSFormState
  needsFullList: boolean
}
export type HookFieldGetList_ReturnType = ListDefinition
// subFeature x
export const HookFieldGetList = "HookField{subFeature}_{fieldKey}_GetList"

type EditorFormSettingsToSetType<RowType> = {
  fieldsToSet: { [key in keyof RowType]: SYSFormField }
  groups: RenderingGroups[]
}

type FormSettingsToSetType<RowType> = {
  fieldsToSet: { [key in keyof RowType]: SYSFormField }
  formIdToSet: string
}

export type HookFormFieldsInit_Type<RowType = any> = {
  formFieldsToSet: { [key in keyof RowType]: SYSFormField }
  /*schema: Schema*/
}
// subFeature x
export const HookFormFieldsInit = "HookForm{subFeature}FieldsInit"

export type HookFormAutoInit_Type<RowType = any> = {
  formType: SYSForm
  formId?: any

  toSet: FormSettingsToSetType<RowType>
}

// subFeature not necessary
export const HookFormAutoInit = "HookFormAutoInit"

export type HookFormEditorInit_Type<RowType = any> = {
  parentField?: SYSField
  toSet: EditorFormSettingsToSetType<RowType>
  formType: string
  formId: string
  feature: SYSFeature
  tableDefId: SYSTableDefIds
}

// subFeature x
export const HookFormEditorInit = "HookForm{subFeature}EditorInit"

export type HookFormEditorAction_Type = {
  formState: SYSFormState
  parentField: SYSField
}

export type HookFormEditorActionReturn_Type = {
  cancel?: boolean
}

// subFeature x
export const HookFormEditorSave = "HookForm{subFeature}EditorSave"

// subFeature x
export const HookFormEditorDelete = "HookForm{subFeature}EditorDelete"

// subFeature x
export const HookFormEditorCancel = "HookForm{subFeature}EditorCancel"

export type HookTableFilterSortSettingsDifferentiator_Type = {
  differentiator: string
  def: SYSTableDef
}
export type HookTableFilterSortSettingsDifferentiator_ReturnType = {
  differentiator: string
}
export const HookTableFilterSortSettingsDifferentiator =
  "HookTableFilterSortSettingsDifferentiator"

// use RefreshLookups if you have a tbale containing lookups and would like to deliver the tabledata cobtaining the lookup columns
// just make sure to define the lookup (LookupDef: Col1: {type""}...)
// it needs to return the root of the lookup object
// currently the Key of The Lookup needs to be the same like the field name

export type HookLookupsRefresh_Type = {
  formState: SYSFormState
  tableDefId: SYSTableDefIds
}
export type HookLookupsRefresh_ReturnType = { [Key: string]: ListDefinition }
export const HookLookupsRefresh = "HookLookupsRefresh"

// use this hook for calculated lookup columns
export type HookLookupRowRefreshed_Type = {
  lookupDefKey: string
  lookupDataRow: any
}
export const HookLookupRowRefreshed = "HookLookupRowRefreshed"

type RefreshAction = "Cancel" | "ForceRefresh" | "Default"
// lets you modify the post body of the lookup request
export type HookFieldLookupPostRefresh_Type<RowType = any> = {
  postToServerData: LookupListPostData
  // set it to false to not start a postback to server
  cancelRefresh: RefreshAction
  //set it to false to not show snack
  showSnack: boolean
  forceClear: boolean
  innerEvent: boolean
  info: {
    _timeStamp: number
    _row: RowType
    _lastPostToServerData: string
  }
}

// subFeature x
export const HookFieldLookupPostRefresh =
  "HookField{subFeature}_{fieldKey}_LookupPostRefresh"

// if a field should not be displayed. Make sure to return a FieldVisibilty - Type
export type HookTableSetColumnVisibility_Type<RowType = any> = {
  def: SYSTableDef
  columnsVisibilityToSet: { [key in keyof RowType]: _ColumnVisibility }
  screen: SYSScreen
  fromInit: boolean
}
export const HookTableSetColumnVisibility = "HookTableSetColumnVisibility"

// if you would like to do the rendering of a cell value on your own
export type HookFieldGetValueRender_Type<RowType = any> = {
  tableDef: SYSTableDef
  rowKey: any
  row: RowType
  columnKey: string
  displayMode: DisplayMode
  operation: GetRowValueOperation
  comp: any
  requestDisplay?: boolean
  formState?: SYSFormState
}
export type HookFieldGetValueRender_ReturnType =
  | {
      res?: any
      valueRaw?: any
    }
  | undefined

// subFeature x (2 calls replace with "" because there is no subFeature at tableDef Level. Should be fine)
export const HookFieldGetValueRender =
  "HookField{subFeature}_{fieldKey}_GetValueRender"

// if you would like to do the rendering of a cell value on your own
export type HookFieldGetEditHint_Type<RowType = any> = {
  row: RowType
  field: SYSField
  formState: SYSFormState
  align: string
  displayMode: EditDisplayMode
}
// return undefined for no hintcontrol rendering
export type HookFieldGetEditHint_ReturnType =
  | TemplateResult
  | TemplateResult[]
  | string

// subFeature x
export const HookFieldGetEditHint =
  "HookField{subFeature}_{fieldKey}_GetEditHint"

// customizing thGroup headers
export type HookTHGroupGetLabelRender_Type = {
  caption: string
}
export type HookTHGroupGetLabelRender_ReturnType = {
  res: string | TemplateResult
  tooltip?: string
  cssClass?: string
}
export const HookTHGroupGetLabelRender = "HookTHGroup_{fieldKey}_GetLabelRender"

// if you would like to do the rendering of a column header resp. field label in edit mode
export type HookFieldGetLabelRender_Type = {
  columnKey: string
  caption: string | DirectiveResult
  align: string
  formState?: SYSFormState
  displayMode: DisplayMode | EditDisplayMode
}
export type HookFieldGetLabelRender_ReturnType = {
  res: TemplateResult | string | DirectiveResult
  valueRaw?: string | DirectiveResult
}

// subFeature x (1 calls replace with "" because there is no subFeature at tableDef Level. Should be fine)
export const HookFieldGetLabelRender =
  "HookField{subFeature}_{fieldKey}_GetLabelRender"

// subFeature x
export const HookFormGetBodyRender = "HookForm{subFeature}GetBodyRender"
export type HookFormGetBodyRender_Type<RowType = any> = {
  formState: SYSFormState
  tableDef: SYSTableDef
  fields: { [key in keyof RowType]: SYSField }
}
export type HookFormGetBodyRender_ReturnType = {
  res: TemplateResult | TemplateResult[]
}

// custom title on dialogs/actions/forms
export type HookTableDialogGetTitleRender_Type<RowType = any> = {
  tableDefId: SYSTableDefIds
  defaultCaption: TemplateResult | string
  placeholders: {
    selectionText: string
    entityName: string
    idInfo: string
    actionName: string
    count: number
    idValue?: any
    idFieldTranslatedName?: string
  }
  rows: { [key: string]: RowType }
  rowKey?: string
  row?: RowType
  actionKey: string
  formState?: SYSFormState
}
export type HookTableDialogGetTitleRender_ReturnType =
  | TemplateResult
  | string
  | undefined
// subFeature x
export const HookTableDialogGetTitleRender =
  "HookTable{subFeature}DialogGetTitleRender"

// custom value on bigedit form. you are also responsible for rendering the label
// check existing controls for stuff todo
// easiest is to copy the existing custom element and change it (eg. textbox.ts)
export type HookFormCustomControlRender_Type = {
  field: SYSField
  formState: SYSFormState
  //readonly: boolean
}
export type HookFormCustomControlRender_ReturnType = TemplateResult

// subFeature x (one call from Tabledef replaced with "")
export const HookFormCustomControlRender =
  "HookForm{subFeature}_{fieldKey}_CustomControlRender"

// gets called for displaying ROW hints/status (warning,...)
export type HookViewRowClass_Type<RowType = any> = {
  rowKey: string
  tableDef: SYSTableDef
  tableData: SYSTableData
  row: RowType
}
export type HookViewRowClass_ReturnType = ViewRowClassType
export const HookViewRowClass = "HookViewRowClass"

// to style the header and row cells in tableview
// this functions must return a object whose key is the fieldKey and the value is the custom classname to set
export type HookViewRowCellClass_Type<RowType = any> = {
  columns: { [key: string]: ColumnDisplayDef }
  row: RowType
  rowKey: string
  displayMode: DisplayMode | EditDisplayMode
  tableDefId?: SYSTableDefIds
  feature?: SYSFeature
  def?: SYSTableDef
}
export type HookViewRowCellClass_ReturnType<RowType = any> = {
  [key in keyof RowType]: CellClass
}

// subFeature x (one from table replaced with "")
export const HookViewRowCellClass = "HookViewRowCellClass{subFeature}"

export type HookViewHeaderCellClass_Type<RowType = any> = {
  columns: { [key in keyof RowType]: ColumnDisplayDef }

  displayMode: DisplayMode | EditDisplayMode
  tableDefId?: SYSTableDefIds
  feature?: SYSFeature
}
export type HookViewHeaderCellClass_ReturnType<RowType = any> = {
  [key in keyof RowType]: CellClass
}
// subFeature x (one from table replaced with "")
export const HookViewHeaderCellClass = "HookViewHeaderCellClass{subFeature}"

export type HookTableServerBeforeMergeRow_Type<RowType = any> = {
  defId: SYSTableDefIds
  rowKey: string
  tableData: SYSTableData
  serverRow: { [key in keyof RowType]: any }
  localRowJustForChecking: { [key in keyof RowType]: any }
}
// return true for handled = canceling further logic
export type HookTableServerBeforeMergeRow_ReturnType = boolean
export const HookTableServerBeforeMergeRow = "HookTableServerBeforeMergeRow"

export type ServerData_ChangedType = {
  isAdd: boolean
  columns: { [key: string]: boolean }
}

export type HookTableServerData_Type = {
  tableDefId: SYSTableDefIds
  type: "RefreshData" | "RefreshRow"
  //contains all the returned keys. check for changed by columns.size>0
  returnedKeys: Map<string, ServerData_ChangedType>
  more: boolean
  ext: any
  tableData: SYSTableData
}
export const HookTableServerData = "HookTableServerData"

export type HookTableCustomTitle_ReturnType =
  | string
  | TemplateResult
  | DirectiveResult
export type HookTableCustomTitle_Type = {
  title: any
  titleAsTemplate: string
  titleText: string
  titleTemplate: string
  setText: string
  setTemplate: string
  sortText: string
  sortTemplate: string
  filterText: string
  filterTemplate: string
  filterTextArray: string[]
  filterAttributeText: string
  filterAttributeTemplate: string
  filterAttributeArray: string[]
  staticFilter: { [key: string]: any }
  forcedFilterKeys: string[]
  forcedFilterContextTitle: string
  defId: SYSTableDefIds
}
export type HookTableFiltersReset_Type = {
  tableDefId: string
  clearedForcedFilterKeys?: string[]
  resetStep: "1:ForcedKeys" | "2:ForcedCustomOrColumn" | "3:Regular"
  favoriteSettings?: { key: string; settings: string }
}
export const HookTableFiltersReset = "HookTableFiltersReset"

export const HookTableCustomTitle = "HookTableCustomTitle"

export type HookTableRowAttributesGetAttributes_Type<RowType = any> = {
  defId: SYSTableDefIds
  row: { [key in keyof RowType]: {} }
  rowAttributeDefKey: string
}
export type HookTableRowAttributesGetAttributes_ReturnType = [] | {}
export const HookTableRowAttributesGetAttributes =
  "HookTableRowAttributesGetAttributes"

// gets all the possible attributes to select from
export type HookTableRowAttributesGetAllAttributes_Type = {
  defId: SYSTableDefIds
  rowAttributeDefKey: string
}
export type HookTableRowAttributesGetAllAttributes_ReturnType = {
  allAttributes: [] | {}
  //titleTranslationKey?: string
}
export const HookTableRowAttributesGetAllAttributes =
  "HookTableRowAttributesGetAllAttributes"

// use this hook to get a nice description which will be displayed
export type HookTableRowAttributeGetProperties_ReturnType =
  HookTablePrepareFn_ReturnTypeEntry
export type HookTableRowAttributeGetProperties_Type = {
  rowAttributeDefKey: string
  defId: SYSTableDefIds
  rowAttributeKey: any
  //count will be the precalculated count if defined so in tableDef
  // so we can display AttributeX(111)
  count?: number
  mode: "TableTitle" | "HeaderMenu"
}
export const HookTableRowAttributeGetProperties =
  "HookTableRowAttributeGetProperties"

export type _HookTableClickedExecutionToSetType = {
  selectionLogic?: SelectRowDefMode | "Ignore"
  expandTextLogic?: "Ignore" | "Default" | "NotDebounced"
}
export type HookTableClicked_Type<RowType = any> = {
  e: Event
  defId: SYSTableDefIds
  // rowKey will be set if inside row is clicked
  // if empty its an indication that we are on the header
  rowKey: string
  row: { [key in keyof RowType]: any }
  columnKey: string
  modifiers: TableClickModifiersType
  expandClicked?: boolean
  linkType?: "OvlHook" | "System" | "None"
  displayType: "Table" | "DetailView"
  executionToSet: _HookTableClickedExecutionToSetType
}

export const HookTableClicked = "HookTableClicked"

// // the eventhandler if the user selected/clicked the cell
// export type HookFieldRowCellSelectedHandler_Type<RowType = any> = {
//   e: Event
//   def: SYSTableDef
//   data: SYSTableData
//   rowKey: string
//   columnKey: string
//   row: RowType
//   displayMode: DisplayMode | EditDisplayMode
//   modifiers: TableClickModifiersType
//   expandClicked?: boolean
//   formState?: SYSFormState
// }
// export type HookFieldRowCellSelectedHandler_ReturnType = boolean
// // subFeature x (one call from table replaced with "")
// export const HookFieldRowCellSelectedHandler =
//   "HookField{subFeature}_{fieldKey}_RowCellSelectedHandler"

// export type HookTableRowSelectedEventControlHandler_Type =
//   HookFieldRowCellSelectedHandler_Type
// export type HookTableRowSelectedEventControlHandler_ReturnType =
//   HookFieldRowCellSelectedHandler_ReturnType

// // subFeature no uses with forms found
// export const HookTableRowSelectedEventControlHandler =
//   "HookTableRowSelectedEventControlHandler"

// export const HookTableRowSelectedHandler = "HookTableRowSelectedHandler"
// export type HookTableRowSelectedHandler_Type<RowType = any> = {
//   e?: Event
//   def: SYSTableDef
//   data: SYSTableData
//   rowKey: string
//   row: RowType
//   modifiers?: TableClickModifiersType

//   customData?: any
//   innerEvent?: boolean
// }
// export type HookTableRowSelectedHandler_ReturnType = boolean

// export type HookFieldHeaderCellSelectedHandler_Type = {
//   e: Event
//   def: SYSTableDef
//   displayMode: DisplayMode | EditDisplayMode
// }
// export type HookFieldHeaderCellSelectedHandler_ReturnType = boolean

// // subFeature no uses with forms found
// export const HookFieldHeaderCellSelectedHandler =
//   "HookField_{fieldKey}_HeaderCellSelectedHandler"

// export type HookFieldHeaderCellSelectedEventControlHandler_Type =
//   HookFieldHeaderCellSelectedHandler_Type
// export type HookFieldHeaderCellSelectedEventControlHandler_ReturnType =
//   HookFieldHeaderCellSelectedHandler_ReturnType

// // subFeature no uses with forms found
// export const HookFieldHeaderCellSelectedEventControlHandler =
//   "HookField_{fieldKey}_HeaderCellSelectedEventControlHandler"

//Form Save/Delete/Postback Functions ####################################
// provide your complete form postback to server logic here if you don't wanna use the default

// export type HookTableCustomSave_Type<RowType = any> = {
//   key: string
//   tableDef: SYSTableDef
//   mode: SaveMode
//   newData: { [key in keyof RowType]: {} }
//   isOfflineRetry: boolean
//   formState: SYSFormState
//   resFiles: FileUploadContainer[]
// }

// //it should return a newId
// export type HookTableCustomSave_ReturnType = string
// export const HookTableCustomSave = "HookTableCustomSave"

// gets called before the form gets saved/posted back to the server
export type HookTableBeforeDelete_Type<RowType = any> = {
  key: string
  idValue: any
  tableDefId: string
  row: RowType
  isOfflineRetry: boolean
}

// use it to an additional validation before save
// or to do a custom save
// continue will just save the result as well (be it only locally if no endpoint or as well on the table endpoint)
// handled will mark the form as undirty and exit (no saving of the table)
export type HookTableBeforeDelete_ReturnType = {
  resolve?: "Cancel" | "Continue" | "DeleteLocally"
  customPostData?: any
  rowFnResult?: TableRowFnProcessRawFeedback_Type
}

export const HookTableBeforeDelete = "HookTableBeforeDelete"

// gets called before the form gets saved/posted back to the server
export type HookTableBeforeSave_Type<RowType = any> = {
  key: string

  mode: SaveMode
  tableDef: TableDataAndDef
  changedFields: { [key in keyof RowType]: {} }
  originalRow: RowType
  changedRow: RowType
  filter: {}
  isOfflineRetry: boolean
  formState: SYSFormState
  resFiles: FileUploadContainer[]
  hasChanges: boolean
}

// use it to an additional validation before save
// or to do a custom save
// continue will just save the result as well (be it only locally if no endpoint or as well on the table endpoint)
// handled will mark the form as undirty and exit (no saving of the table)
export type HookTableBeforeSave_ReturnType = {
  resolve?:
    | "Cancel"
    | "Handled"
    | "Error"
    | "Continue"
    | "SaveLocally"
    | "ForceSaveAndContinue"

  customPostData?: any
  rowFnResult?: TableRowFnProcessRawFeedback_Type
}

export type HookFormBeforeCancel_ReturnType = {
  resolve: "Cancel" | "Continue"
}
export type HookFormBeforeCancel_Type = { formState: SYSFormState }
export const HookFormBeforeCancel = "HookFormBeforeCancel"

export type HookFormAfterCancel_Type = { formState: SYSFormState }
export const HookFormAfterCancel = "HookFormAfterCancel"

export const HookTableBeforeSave = "HookTableBeforeSave"

// // if you would like to process save errors yourself
// export type HookTableSaveError_ReturnType = {
//   resolve: "ValidationError" | "Continue"
// }

export type HookTableSaveError_Type = {
  rowFnResult: TableRowFnProcessRawFeedback_Type
  originatingBatchKey: string
  currentKey: string
  def: SYSTableDef
  data: SYSTableData
  formState: SYSFormState
  mode: SaveMode
  isOfflineRetry: boolean
  resFiles: FileUploadContainer[]
}
export const HookTableSaveError = "HookTableSaveError"

// gets called after save
// use it to refresh other data as well (eg. translations use case)
export type HookTableAfterSave_Type<RowType = any> = {
  rowFnResult: TableRowFnProcessRawFeedback_Type
  key: string
  def: SYSTableDef
  data: SYSTableData
  mode: SaveMode
  res: { data: RowType[]; ext: any }
  isOfflineRetry: boolean
  formState: SYSFormState
  //contains all the returned keys. check for changed by columns.size>0
  returnedKeys: Map<string, ServerData_ChangedType>
  resFiles: FileUploadContainer[]
}

export const HookTableAfterSave = "HookTableAfterSave"

// if you would like to process delete offline mode yourself
export type HookTableDeleteOffline_Type = {
  key: string
  tableDef: SYSTableDef
  res: SYSTableData
  isOfflineRetry: boolean
}
// export type HookTableDeleteOffline_ReturnType = HookTableSaveError_ReturnType
export const HookTableDeleteOffline = "HookTableDeleteOffline"

// if you would like to process delete errors yourself
export type HookTableDeleteError_Type = {
  rowFnResult: TableRowFnProcessRawFeedback_Type
  key: string
  tableDef: SYSTableDef
  res: any
}

export const HookTableDeleteError = "HookTableDeleteError"

export type HookTableRowsAfterRebuild_Type<RowType = any> = {
  def: SYSTableDef
  rows: { [key: string]: RowType }
  rowKeys: string[]
}
export const HookTableRowsAfterRebuild = "HookTableRowsAfterRebuild"

// if you would like to do smth after delete
export type HookTableAfterDelete_Type<RowType = any> = {
  key: string
  def: SYSTableDef
  data: SYSTableData
  res: any
  deletedRow: RowType
  isOfflineRetry: boolean
}
export const HookTableAfterDelete = "HookTableAfterDelete"

export type HookTableGroupValue_Type<RowType = any> = {
  def: SYSTableDef
  data: SYSTableData
  row: RowType
}
export type HookTableGroupValue_ReturnType = string
export const HookTableGroupValue = "HookTableGroup_{groupKey}_Value"

export type HookTableGroupCalcValue_ReturnType = string | number
export type HookTableGroupCalcValue_Type<RowType = any> = {
  def: SYSTableDef
  data: SYSTableData
  row: RowType
}

export type HookTableGroupSortValue_Type = HookTableGroupValue_Type
export type HookTableGroupSortValue_ReturnType = any
export const HookTableGroupSortValue = "HookTableGroupSort_{groupKey}_Value"

export const HookTableGroupCalcValue = "HookTableGroup_{aggregateKey}_CalcValue"

export type _GroupHeaderFooterColumnType = {
  value: any
  cssClass?: string
  colSpan?: number
}

export type _TableGroupColumnsToSetType<RowType = any> = {
  [key in keyof RowType]: _GroupHeaderFooterColumnType
}
// export type GroupHeaderFooterResultToSetType<RowType = any> = {
//   // groups contains the groups covered by this headr/footer
//   // (remember can be more than 1 header/footer for condensed header/footer s)
//   // and we need to know the groups covered for at least setting a nice css class
//   cssClassToSet: string
//   _type: GroupHeaderFooterBreaksValueType
//   valuesToSet:
//     | _TableGroupColumnsToSetType<RowType>
//     | _GroupHeaderFooterColumnType
//     | TemplateResult
// }

export type HookTableGroupHeadersFooters_Type<RowType = any> = {
  def: SYSTableDef
  data: SYSTableData
  grouping: {
    groups: { [key: string]: GroupBreakType }
    fullGroupAccessStrings: string[]
    visibleColumns: string[]
  }
  allGrouping: {
    [key: string]: {
      isBreak?: boolean
      isFirstRow?: boolean
      isLastRow?: boolean
      groups?: { [key: string]: GroupBreakType }
      groupAccessStrings?: string[]
    }
  }
  row: RowType
  previousRowKey: string
  nextRowKey: string
  rowKey: string
  isFirstRow: boolean
  isLastRow: boolean
  results: any[]
  getTemplate(
    type: "columns",
    columnsToSet?: _TableGroupColumnsToSetType
  ): {
    cssClassToSet: string
    _type: GroupHeaderFooterBreaksValueType
    valueToSet: _TableGroupColumnsToSetType<RowType>
  }
  getTemplate(
    type: "custom",
    columnsToSet?: _TableGroupColumnsToSetType
  ): {
    cssClassToSet: string
    _type: GroupHeaderFooterBreaksValueType
    valueToSet: TemplateResult
  }
  getTemplate(
    type: GroupHeaderFooterBreaksValueType,
    columnsToSet?: _TableGroupColumnsToSetType
  ):
    | {
        cssClassToSet: string
        _type: GroupHeaderFooterBreaksValueType
        valueToSet: _TableGroupColumnsToSetType<RowType>
      }
    | {
        cssClassToSet: string
        _type: GroupHeaderFooterBreaksValueType
        valueToSet: TemplateResult
      }
}

export const HookTableGroupHeaders = "HookTableGroupHeaders"
export const HookTableGroupFooters = "HookTableGroupFooters"

export const HookTableFormDataLoaded = "HookTableFormDataLoaded"
export type HookTableFormDataLoaded_Type<RowType = any> = {
  mode: "Add" | "Update"
  tableDef: _TableGetReturnType<RowType>
  fresh: boolean
  formState: SYSFormState
}
